import { useState } from "react";
import { FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { safeHttpUrl } from "@/lib/images";
import type { EntityActivityAttachment } from "@adva/types";

function isImageMime(mime: string | null | undefined): boolean {
  return !!mime && mime.startsWith("image/");
}

export function AttachmentStrip({
  attachments,
  size = "sm",
}: {
  // Unified across comm attachments and media items — the normalized
  // EntityActivityAttachment (no DB `id`; keyed on index). CommunicationAttachment
  // is a structural superset, so existing comm callers still type-check.
  attachments: EntityActivityAttachment[];
  // Media (job-site photos) render as a first-class, larger thumbnail per
  // Ryan's "pictures are super important" directive; comm attachments stay
  // compact. Both within operator density (§I8.1: inline thumbs are the densest
  // imagery slot).
  size?: "sm" | "md";
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  if (attachments.length === 0) return null;

  const active = openIndex != null ? attachments[openIndex] : null;
  const thumb = size === "md" ? "size-24" : "size-16";

  return (
    <>
      <div className="flex flex-wrap gap-2 mt-1">
        {attachments.map((att, i) => {
          const safeUrl = safeHttpUrl(att.url);
          if (isImageMime(att.mime_type) && safeUrl) {
            return (
              <button
                key={i}
                type="button"
                onClick={() => setOpenIndex(i)}
                className={cn(
                  "overflow-hidden rounded-[var(--radius-md)] border border-[color:var(--color-border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]",
                  thumb,
                )}
                aria-label={att.caption ?? "Open attachment"}
              >
                <img
                  src={safeUrl}
                  alt={att.caption ?? ""}
                  className="size-full object-cover"
                  loading="lazy"
                />
              </button>
            );
          }
          const label = att.caption ?? "Attachment";
          // Non-http(s) URLs render as non-clickable text — never as an href —
          // so a `javascript:`/`data:` scheme cannot execute on click.
          if (!safeUrl) {
            return (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[color:var(--color-border)] px-2 py-1 typo-label text-[color:var(--color-text-md)]"
                title={label}
              >
                <FileText className="size-3.5" aria-hidden />
                {label}
              </span>
            );
          }
          return (
            <a
              key={i}
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[color:var(--color-border)] px-2 py-1 typo-label text-[color:var(--color-text-md)] hover:text-[color:var(--color-text-hi)]"
            >
              <FileText className="size-3.5" aria-hidden />
              {label}
            </a>
          );
        })}
      </div>

      <Dialog
        open={openIndex != null}
        onOpenChange={(o) => !o && setOpenIndex(null)}
      >
        <DialogContent className="max-w-3xl p-0 bg-[color:var(--color-surface)] border-[color:var(--color-border)]">
          <DialogTitle className="sr-only">
            {active?.caption ?? "Attachment"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Attachment preview
          </DialogDescription>
          {active && safeHttpUrl(active.url) && (
            <div className="flex flex-col">
              <img
                src={safeHttpUrl(active.url) ?? undefined}
                alt={active.caption ?? ""}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              {active.caption && (
                <div className="px-4 py-3 typo-body text-[color:var(--color-text-md)] border-t border-[color:var(--color-border)]">
                  {active.caption}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
